import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useTranslation } from "react-i18next";

const ScatterChart = ({ data, selectedTrip, tripRte, tripDir, tripStart, inbound }) => {

  const [t] = useTranslation('common');

  const chartRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);

  let in_label = inbound;

  useEffect(() => {

    const svg = d3.select(chartRef.current);

    svg.selectAll("*").remove();

    let sorted = data;

    let realPointsY = sorted.map((e) => e.real_stop_time);

    let plannedData = [];
    let unique_sdt = [...new Set(sorted.map(i => i.shape_dist_traveled))];

    unique_sdt.forEach((e) => {
      let result = sorted.filter((f) => f.shape_dist_traveled == e);
      plannedData.push(result[0]);
    });

    let plannedPoints = plannedData.map((e) => ({
      real_stop_time: e.planned_stop_time,
      stop_name: e.stop_name,
      shape_dist_traveled: e.shape_dist_traveled,
      status: e.status,
      diff: e.diff,
      date: 'Planifié',
      timepoint: e.timepoint
    }));

    let tickLabels = plannedPoints.map((e) => e.stop_name);
    let tickLabelsFormat = [tickLabels[0], tickLabels[tickLabels.length - 1]];

    let chartData = [
      { name: 'real', values: sorted },
      { name: 'planned', values: plannedPoints }
    ];

    let timeFormat = d3.timeFormat('%H:%M');

    realPointsY.push(Math.min(...realPointsY) - 900);
    realPointsY.push(Math.max(...realPointsY) + 900);

    let realTimes = realPointsY.map(i => new Date(i * 1000).toISOString().substring(11, 19));

    let parsedDateTimes = realTimes.map(d => {
      if (['00','01','02','03'].includes(d.substring(0,2))) {
        return new Date('2023-07-14 ' + d);
      } else {
        return new Date('2023-07-13 ' + d);
      }
    });

    const margins = { top: 100, right: 75, bottom: 50, left: 75 };
    const width = chartRef.current.parentElement.offsetWidth;
    const height = chartRef.current.parentElement.offsetHeight;

    // Prevent broken render (fix #5)
    if (!width || !height) return;

    const innerWidth = width - margins.left - margins.right;
    const innerHeight = height - margins.top - margins.bottom;

    let xScale = d3.scaleLinear()
      .domain([d3.min(unique_sdt), d3.max(unique_sdt)])
      .range([0, innerWidth]);

    let yScale = d3.scaleTime()
      .domain(d3.extent(parsedDateTimes))
      .range([innerHeight, 0]);

    const chart = svg
      .attr('width', innerWidth + margins.right + margins.left)
      .attr('height', height + margins.top + margins.bottom)
      .append('g')
      .attr('transform', `translate(${margins.left},${margins.top})`);

    // Background
    chart.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr('transform', `translate(${-margins.left},${-margins.top})`)
      .attr("fill", "white")
      .attr("stroke", "#dee2e6")
      .attr("rx", 6)
      .attr("ry", 6);

    let tooltip = d3.select(chartRef.current.parentElement)
      .select('.tooltipotp');

    if (tooltip.empty()) {
      tooltip = d3.select(chartRef.current.parentElement)
        .append('div')
        .attr('class', 'tooltipotp')
        .attr('id', 'tooltipid');
    }

    const line = d3.line()
      .x(d => xScale(d.shape_dist_traveled))
      .y(d => {
        let realTime = new Date(d.real_stop_time * 1000).toISOString().substring(11, 18);
        let parsedDateTime;
        if (['00','01','02','03'].includes(realTime.substring(0,2))) {
          parsedDateTime = new Date('2023-07-14 ' + realTime);
        } else {
          parsedDateTime = new Date('2023-07-13 ' + realTime);
        }
        return yScale(parsedDateTime);
      });

    chart.selectAll("myLines")
      .data(chartData)
      .join("path")
      .attr("d", d => line(d.values))
      .attr("stroke", d => d.name === 'planned' ? 'black' : 'none')
      .attr('transform', 'translate(10,0)')
      .style("stroke-width", 1)
      .style("fill", "none");

    chart.selectAll('myDots')
      .data(chartData)
      .join('g')
      .style('fill', d => d.name === 'planned' ? 'white' : 'red')
      .style('stroke', d => d.name === 'planned' ? 'black' : 'none')
      .selectAll('myPoints')
      .data(d => d.values)
      .join('circle')
      .attr("cx", d => xScale(d.shape_dist_traveled))
      .attr("cy", d => {
        let realTime = new Date(d.real_stop_time * 1000).toISOString().substring(11, 18);
        let parsedDateTime;
        if (['00','01','02','03'].includes(realTime.substring(0,2))) {
          parsedDateTime = new Date('2023-07-14 ' + realTime);
        } else {
          parsedDateTime = new Date('2023-07-13 ' + realTime);
        }
        return yScale(parsedDateTime);
      })
      .attr("r", 3)
      .attr('transform', 'translate(10,0)')
      .on('mouseover', (event, d) => {
        d3.select(event.target).attr("r", 10);

        const [xPos, yPos] = d3.pointer(event);

        tooltip.html(
          'Date : ' + d.date + '<br/>' +
          t('tripotp.hover.stop') + d.stop_name + '<br/>' +
          t('tripotp.hover.time') + new Date(d.real_stop_time * 1000).toISOString().substring(11, 19)
        )
          .style('left', xPos + 'px')
          .style('top', yPos - 100 + 'px')
          .style('opacity', 0.9);
      })
      .on('mouseout', (event) => {
        d3.select(event.target).attr("r", 3);
        tooltip.style('opacity', 0);
      });

    chart.append('g')
      .attr('transform', `translate(10,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .tickValues([xScale.domain()[0], xScale.domain()[1]])
          .tickFormat((d, i) => width > 999 ? tickLabelsFormat[i] : '')
      );

    chart.append('g')
      .call(
        d3.axisLeft(yScale)
          .tickFormat(timeFormat)
          .ticks(d3.timeMinute.every(10))
      );

    chart.append("text")
      .attr("x", (innerWidth / 2))
      .attr("y", -margins.top / 2)
      .attr("text-anchor", "middle")
      .style("font-size", "18px")
      .text(
        t('tripotp.route') + ' ' + tripRte +
        ' direction ' + (tripDir == in_label ? t('tripotp.in') : t('tripotp.out')) +
        ' ' + t('tripotp.at') + ' ' + tripStart
      );

    // Namespaced resize (fix #2)
    function updateGraph() {
      d3.select(chartRef.current).selectAll("*").remove();
      setRefreshKey(k => k + 1);
    }

    d3.select(window).on('resize.scatterchart', updateGraph);

    return () => {
      d3.select(window).on('resize.scatterchart', null);
    };

  }, [data, refreshKey]);

  return <svg ref={chartRef}></svg>;
};

export default ScatterChart;